import { useEffect, useState } from 'react';
import {
  InfoCard,
  Link,
  Progress,
  ResponseErrorPanel,
  Table,
  TableColumn,
} from '@backstage/core-components';
import {
  Box,
  makeStyles,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@material-ui/core';
import MergeTypeIcon from '@material-ui/icons/MergeType';
import DraftsIcon from '@material-ui/icons/Drafts';
import { cockpitTokens } from './cockpitTokens';
import { PR_WIDGET_ANCHOR_ID, scrollToAnchor } from './anchors';
import { updatedLabel } from './updatedLabel';
import {
  countOf,
  ListState,
  PullRequest,
  usePullRequestLists,
} from './pullRequestLists';

const statusColumn: TableColumn<PullRequest> = {
  title: '',
  field: 'isDraft',
  width: '48px',
  sorting: false,
  render: row => (
    <Box display="flex" justifyContent="center">
      {row.isDraft ? (
        <Tooltip title="draft">
          <DraftsIcon
            fontSize="small"
            style={{ color: 'var(--ui-text-muted)' }}
          />
        </Tooltip>
      ) : (
        <Tooltip title="open">
          <MergeTypeIcon fontSize="small" style={{ color: '#2EA043' }} />
        </Tooltip>
      )}
    </Box>
  ),
};

const updatedColumn: TableColumn<PullRequest> = {
  title: 'Updated',
  field: 'updatedAt',
  type: 'datetime',
  width: '104px',
  render: row =>
    row.updatedAt ? (
      <span title={new Date(row.updatedAt).toLocaleString()}>
        {updatedLabel(row.updatedAt)}
      </span>
    ) : (
      '—'
    ),
};

/**
 * The pull request itself, on two lines: its title, and beneath it where it
 * lives and — on a list of somebody else's work — who opened it.
 *
 * Two lines rather than a column each, because the card shares its row with the
 * other list: at half the page, five columns squeezed the title to a word per
 * line and pushed the last column out of the card. The contents are the same.
 */
const pullRequestColumn = (showAuthor: boolean): TableColumn<PullRequest> => ({
  title: 'Pull request',
  field: 'title',
  // The table's search reads a column's field, which is the title alone; what
  // the entry shows beneath it has to be searchable too.
  customFilterAndSearch: (term: string, row: PullRequest) =>
    [row.title, row.repo, `#${row.number}`, showAuthor ? row.author : undefined]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(String(term).toLowerCase()),
  render: row => (
    <Box display="flex" flexDirection="column" style={{ gap: 2 }}>
      <Link to={row.htmlUrl}>{row.title}</Link>
      <Typography variant="caption" color="textSecondary">
        {row.repo ?? '—'} <Link to={row.htmlUrl}>#{row.number}</Link>
        {showAuthor && row.author ? ` · by ${row.author}` : ''}
      </Typography>
    </Box>
  ),
});

/** One of the reader's two lists: where it comes from and what it says. */
type ListSpec = {
  /** The tab's label. */
  label: string;
  /** Whether the list is somebody else's work, so who opened each one matters. */
  showAuthor: boolean;
  /** What the tab says when the list is empty. */
  empty: string;
  /** What an unlinked account keeps from showing up, e.g. "your pull requests". */
  unlinked: string;
};

const MINE: ListSpec = {
  label: 'My open pull requests',
  showAuthor: false,
  empty: 'You have no open pull requests. 🎉',
  unlinked: 'your pull requests',
};

// It answers "what is waiting on me": GitHub drops a pull request from this list
// once the reader has submitted a review, and a re-requested review brings it
// back. Somebody else's work, so each entry names who opened it.
const REVIEWS: ListSpec = {
  label: 'Review requested',
  showAuthor: true,
  empty: 'Nothing is waiting for your review.',
  unlinked: 'the reviews waiting on you',
};

const useStyles = makeStyles(theme => ({
  // The page's own section tabs set the look (HomePage): the same weight,
  // colours and indicator, so the two sets of tabs read as one family.
  tabs: {
    borderBottom: `1px solid ${cockpitTokens.color.border}`,
    padding: theme.spacing(0, 1),
  },
  tabsIndicator: {
    backgroundColor: cockpitTokens.color.brand,
  },
  tab: {
    color: cockpitTokens.color.textMuted,
    fontWeight: 600,
    textTransform: 'none',
    // The theme's own hover fill turns light in the dark appearance, which
    // made the tab under the pointer the brightest thing on the card.
    '&:hover': {
      color: cockpitTokens.color.text,
      backgroundColor: cockpitTokens.color.surfaceStrong,
    },
  },
  tabSelected: {
    color: cockpitTokens.color.text,
  },
  tabLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  count: {
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: 1,
    padding: '3px 7px',
    borderRadius: cockpitTokens.radius.pill,
    background: cockpitTokens.color.surfaceStrong,
    color: cockpitTokens.color.textMuted,
  },
  // Reviews waiting stand out, so the tab that is not open by default is not
  // where they get lost.
  countWaiting: {
    background: cockpitTokens.color.buttonPrimary,
    color: cockpitTokens.color.buttonPrimaryText,
  },
  notice: {
    padding: theme.spacing(1.5, 2, 0),
  },
  said: {
    padding: theme.spacing(2),
  },
}));

const TabLabel = ({
  spec,
  state,
  waiting,
}: {
  spec: ListSpec;
  state: ListState;
  waiting?: boolean;
}) => {
  const classes = useStyles();
  // The same count the counts row states, so the two cannot disagree: none at
  // all while it is not known. The list stops at fifty; past that the tab says
  // how many of the total it holds.
  const total = countOf(state);
  if (typeof total !== 'number') {
    return <span className={classes.tabLabel}>{spec.label}</span>;
  }
  const listed = state.data?.pullRequests.length ?? 0;
  const setApart = waiting && total > 0;
  return (
    <span className={classes.tabLabel}>
      {spec.label}
      <span
        className={`${classes.count} ${setApart ? classes.countWaiting : ''}`}
        // Read by the tests instead of the generated class names, which JSS
        // hashes in a production build.
        data-count
        data-waiting={setApart ? 'true' : undefined}
      >
        {total > listed ? `${listed} of ${total}` : `${listed}`}
      </span>
    </span>
  );
};

// The card is the one around the tabs, so the table draws no card of its own.
const Unframed = (props: object) => <div {...props} />;

/** One list in its tab: each of its states said in words. */
const PullRequestList = ({
  spec,
  state,
}: {
  spec: ListSpec;
  state: ListState;
}) => {
  const classes = useStyles();
  const { loading, error, data } = state;

  if (loading) return <Progress />;

  if (error) {
    return (
      <Box p={2}>
        <ResponseErrorPanel error={error} />
      </Box>
    );
  }

  if (data && !data.mapped) {
    return (
      <Typography
        variant="body2"
        color="textSecondary"
        className={classes.said}
      >
        No GitHub account is linked to your profile yet. Ask an admin to set
        your <code>github_username</code> so {spec.unlinked} show up here.
      </Typography>
    );
  }

  const pullRequests = data?.pullRequests ?? [];
  const unreachable = data?.unreachable ?? [];
  // A filter from six entries, pages from eleven: what the card offered before.
  const searchable = pullRequests.length > 5;
  const pages = pullRequests.length > 10;

  return (
    <>
      {/* An organisation that could not be asked is named rather than left out:
          a list silently missing one reads as "nothing waiting there"
          (specs/home-page.md AC-21). */}
      {unreachable.length > 0 && (
        <Typography
          variant="body2"
          color="textSecondary"
          className={classes.notice}
        >
          Couldn't ask {unreachable.join(', ')}, so this list may be incomplete.
        </Typography>
      )}
      <Table<PullRequest>
        columns={[
          statusColumn,
          pullRequestColumn(spec.showAuthor),
          updatedColumn,
        ]}
        data={pullRequests}
        components={{ Container: Unframed }}
        options={{
          // Search and pages only once the list is long enough to need them.
          toolbar: searchable,
          search: searchable,
          paging: pages,
          pageSize: 10,
          padding: 'dense',
        }}
        emptyContent={
          <Typography
            variant="body2"
            color="textSecondary"
            className={classes.said}
          >
            {spec.empty}
          </Typography>
        }
      />
    </>
  );
};

/**
 * The reader's pull requests on the home page, across every organisation the
 * portal serves (DX-391): their own open pull requests, and in a second tab the
 * ones waiting on their review. The GitHub login is resolved server-side from the
 * Backstage identity, so there is no GitHub login here.
 *
 * One card with two tabs rather than two cards side by side: on the many days
 * nothing waits for review, a card of its own stood empty in the best place on
 * the page. The card opens on the reader's own list every time; the review tab
 * carries its count, highlighted while reviews wait, and so does the counts row
 * above (specs/home-page.md AC-6, AC-23). Both lists come from the page's
 * `PullRequestListsProvider`, asked once for the card and the counts row alike,
 * so the count is there before the tab is opened.
 */
export const PullRequestsCard = () => {
  const classes = useStyles();
  const { mine, reviews, reviewsAsked } = usePullRequestLists();
  const [tab, setTab] = useState<'mine' | 'reviews'>('mine');

  // Asked for from elsewhere on the page — the open-reviews figure, or the
  // attention card's row for the reviews it had no room for: the card turns to
  // its review tab and comes into view, so the figure leads to the list it
  // counts (specs/home-page.md AC-7). It still opens on the reader's own list
  // (AC-23); only an explicit request turns it.
  useEffect(() => {
    if (reviewsAsked === 0) return;
    setTab('reviews');
    scrollToAnchor(PR_WIDGET_ANCHOR_ID);
  }, [reviewsAsked]);

  return (
    <InfoCard noPadding>
      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        aria-label="Your pull requests"
        classes={{ root: classes.tabs, indicator: classes.tabsIndicator }}
      >
        <Tab
          value="mine"
          classes={{ root: classes.tab, selected: classes.tabSelected }}
          id="pull-requests-tab-mine"
          aria-controls="pull-requests-panel"
          label={<TabLabel spec={MINE} state={mine} />}
        />
        <Tab
          value="reviews"
          classes={{ root: classes.tab, selected: classes.tabSelected }}
          id="pull-requests-tab-reviews"
          aria-controls="pull-requests-panel"
          label={<TabLabel spec={REVIEWS} state={reviews} waiting />}
        />
      </Tabs>
      <div
        role="tabpanel"
        id="pull-requests-panel"
        aria-labelledby={`pull-requests-tab-${tab}`}
      >
        {tab === 'mine' ? (
          <PullRequestList spec={MINE} state={mine} />
        ) : (
          <PullRequestList spec={REVIEWS} state={reviews} />
        )}
      </div>
    </InfoCard>
  );
};
